import React from "react";
import { BrowserRouter as Router, Routes, Route, useLocation } from "react-router-dom";
import Header from "./Component/Header";
import PrivateRoute from "./PrivateRoute";
import RoleBasedRoute, { isAdminOrHR, canManageLeaves } from "./RoleBasedRoute";

// Pages
import Index from "./Pages/Index";
import CreateReportForm from "./Pages/CreateReportForm";
import ClientQueriesTable from "./Pages/ClientQueriesTable";
import EmployeeCards from "./Pages/EmployeeCards";
import IncentiveConfiguration from "./Pages/IncentiveConfiguration";
import ReportSubmission from "./Pages/ReportSubmissionTable";
import StoreManagementApp from "./Pages/StoreManagementForm";
import StoreReportsTable from "./Pages/StoreReportsTable";
import SuspiciousTransactionAlerts from "./Pages/SuspiciousTransactionAlerts";
import TriggerAlerts from "./Pages/TriggerAlerts";
import SurvillEmployeeLogin from "./Pages/SurvillEmployeeLogin";
import CreateEditReportForm from "./Pages/CreateEditReportForm";
import CashierAssistancePage from "./Pages/CashierAssistancePage";
import SingleTransactionPage from "./Pages/SingleTransactionPage";
import StoresPage from "./Pages/StoresPage";
import EmployeeManagementForm from "./Pages/EmployeeManagementForm";
import StoreManagementForm from "./Pages/StoreManagementForm";
import SingleStorePage from "./Pages/SingleStorePage";
import EmployeePage from "./Pages/EmployeePage";
import GlobalLoader from './Component/GlobalLoader';
import ClientQueriesPage from "./Pages/ClientQueriesPage";
import CreateEditClientQueryForm from "./Pages/CreateEditClientQueryForm";
import StoreNotesPage from "./Pages/StoreNotesPage";
import NotificationsPage from "./Pages/NotificationsPage";
import AnnouncementsPage from "./Pages/AnnouncementsPage";
import DeviceManagement from "./Pages/DeviceManagement";
import LicenseManagement from "./Pages/LicenseManagement";
import PosBridgePCs from "./Pages/PosBridgePCs";

// Attendance & Leave
import LeaveApplication from "./Component/LeaveApplication";
import LeaveStatus from "./Component/LeaveStatus";
import HRLeaveManagement from "./Component/HRLeaveManagement";
import TodayAttendanceDashboard from "./Component/TodayAttendanceDashboard";
import MyAttendance from "./Component/MyAttendance";
import EmployeeAttendanceSummary from "./Component/EmployeeAttendanceSummary";

/*
 * RANK REFERENCE (mirrors backend permissions.py):
 *   10 = ADMIN
 *    9 = Department Heads (OPS_HEAD_ADMIN, HR_HEAD, SALES_HEAD, TECH_ADMIN, etc.)
 *    7 = Managers (MANAGER, HR_MANAGER, TECH_MANAGER, DEVELOPMENT_MANAGER, FLOOR_SUPERVISOR)
 *    5 = Team Managers / Coordinators (TEAM_MANAGER, HR_COORDINATOR, FLOOR_COORDINATOR)
 *    3 = Executives / Assistants
 *    2 = SALES_ASSISTANT, SPA
 *    1 = PA
 *
 * PERMISSION MODEL:
 *   Attendance view    — rank ≥ 5, same dept, viewer outranks target (backend enforces dept/rank)
 *   Attendance update  — ADMIN and HR ONLY
 *   Leave approval L1  — rank ≥ 5, same dept, viewer outranks target
 *   Leave approval L2  — HR roles + ADMIN
 */

function App() {
  return (
    <Router>
      <AppContent />
    </Router>
  );
}

function AppContent() {
  const location = useLocation();
  const { pathname } = location;

  const isLogin           = pathname === "/login";
  const isNewReport       = pathname === "/reports/new";
  const isEditReport      = /^\/reports\/[^/]+\/edit$/.test(pathname);
  const isStoreManagement = pathname === "/storemanagementapp";
  const isStoreEdit       = /^\/stores\/[^/]+\/edit$/.test(pathname);
  const isNewEmployee     = pathname === "/employees/new";
  const isEditEmployee    = /^\/employees\/[^/]+\/edit$/.test(pathname);
  const isNewClientQuery  = pathname === "/client-queries/new";
  const isEditClientQuery = /^\/client-queries\/[^/]+\/edit$/.test(pathname);

  const shouldHideHeader =
    isLogin || isNewReport || isEditReport || isStoreManagement ||
    isStoreEdit || isNewEmployee || isEditEmployee ||
    isNewClientQuery || isEditClientQuery;

  const shouldShowLoader = () => {
    const loaderPages = [
      "/reports/new",
      /^\/reports\/[^/]+\/edit$/,
      /^\/transaction\/[^/]+$/,
      /^\/stores\/[^/]+$/,
      /^\/employees\/[^/]+$/,
      "/employees/new",
      /^\/employees\/[^/]+\/edit$/,
      "/storemanagementapp",
      /^\/stores\/[^/]+\/edit$/,
      "/createreportform",
      "/suspicioustransactionalerts",
      "/client-queries/new",
      /^\/client-queries\/[^/]+\/edit$/,
    ];
    return loaderPages.some((page) =>
      typeof page === 'string' ? pathname === page : page.test(pathname)
    );
  };

  return (
    <div className="App" style={!shouldHideHeader ? { paddingTop: '73px', overflow: 'hidden' } : {}}>
      {!shouldHideHeader && <Header />}
      {shouldShowLoader() && <GlobalLoader />}

      <Routes>
        <Route path="/login" element={<SurvillEmployeeLogin />} />

        <Route element={<PrivateRoute />}>
          <Route path="/" element={<Index />} />
          <Route path="/dashboard" element={<Index />} />
          <Route path="/client-queries/new" element={<CreateEditClientQueryForm />} />
          <Route path="/client-queries/:queryId/edit" element={<CreateEditClientQueryForm />} />
          <Route path="/createreportform" element={<CreateReportForm />} />
          <Route path="/clientqueriestable" element={<ClientQueriesPage />} />

          {/* Employee Cards — Team Manager and above (rank ≥ 5) */}
          <Route
            path="/employeecards"
            element={
              <RoleBasedRoute minRank={5}>
                <EmployeeCards />
              </RoleBasedRoute>
            }
          />

          {/* Stores — Admin only */}
          <Route
            path="/stores"
            element={
              <RoleBasedRoute adminOnly={true}>
                <StoresPage />
              </RoleBasedRoute>
            }
          />

          <Route path="/incentiveconfiguration" element={<IncentiveConfiguration />} />
          <Route path="/reportsubmissiontable" element={<ReportSubmission />} />

          {/* Store Management — Admin only */}
          <Route
            path="/storemanagementapp"
            element={
              <RoleBasedRoute adminOnly={true}>
                <StoreManagementApp />
              </RoleBasedRoute>
            }
          />
          <Route
            path="/stores/:storeId/edit"
            element={
              <RoleBasedRoute adminOnly={true}>
                <StoreManagementForm />
              </RoleBasedRoute>
            }
          />

          <Route path="/storereportstable" element={<StoreReportsTable />} />
          <Route path="/suspicioustransactionalerts" element={<SuspiciousTransactionAlerts />} />
          <Route path="/triggeralerts" element={<TriggerAlerts />} />
          <Route path="/reports/new" element={<CreateEditReportForm />} />
          <Route path="/cashier-assistance" element={<CashierAssistancePage />} />
          <Route path="/reports/:reportId/edit" element={<CreateEditReportForm />} />
          <Route path="/transaction/:transactionId" element={<SingleTransactionPage />} />
          <Route path="/stores/:storeId" element={<SingleStorePage />} />

          {/* Employee Management — Admin or HR */}
          <Route
            path="/employees/new"
            element={
              <RoleBasedRoute roleCheck={isAdminOrHR}>
                <EmployeeManagementForm />
              </RoleBasedRoute>
            }
          />
          <Route
            path="/employees/:employeeId/edit"
            element={
              <RoleBasedRoute roleCheck={isAdminOrHR}>
                <EmployeeManagementForm />
              </RoleBasedRoute>
            }
          />

          <Route path="/employees/:employeeId" element={<EmployeePage />} />
          <Route path="/store-notes" element={<StoreNotesPage />} />
          <Route path="/notifications" element={<NotificationsPage />} />
          <Route path="/announcements" element={<AnnouncementsPage />} />

          {/* Device Management — Admin only */}
          <Route
            path="/device-management"
            element={
              <RoleBasedRoute adminOnly={true}>
                <DeviceManagement />
              </RoleBasedRoute>
            }
          />

          {/* Software licences (VMS / Edge Recording Manager / POS Bridge) — Admin only */}
          <Route
            path="/licenses"
            element={
              <RoleBasedRoute adminOnly={true}>
                <LicenseManagement />
              </RoleBasedRoute>
            }
          />

          {/* POS Bridge PCs: setup codes, cut off, switch-over from the old tokens — Admin only */}
          <Route
            path="/pos-bridge-pcs"
            element={
              <RoleBasedRoute adminOnly={true}>
                <PosBridgePCs />
              </RoleBasedRoute>
            }
          />

          {/* ── ATTENDANCE ───────────────────────────────────────────────────── */}

          {/* Attendance Dashboard — Team Manager and above (rank ≥ 5)
              Records returned by backend are already scoped to the viewer's
              department and rank — no extra frontend filtering required. */}
          <Route
            path="/admin/attendance"
            element={
              <RoleBasedRoute minRank={5}>
                <TodayAttendanceDashboard />
              </RoleBasedRoute>
            }
          />

          {/* Employee Attendance Summary — Team Manager and above (rank ≥ 5)
              Backend rejects 403 if viewer tries to see someone they outrank. */}
          <Route
            path="/admin/summary"
            element={
              <RoleBasedRoute minRank={5}>
                <EmployeeAttendanceSummary />
              </RoleBasedRoute>
            }
          />

          {/* My Attendance — all logged-in employees */}
          <Route path="/employee/attendance" element={<MyAttendance />} />

          {/* ── LEAVE MANAGEMENT ─────────────────────────────────────────────── */}

          {/* Apply for Leave — all employees */}
          <Route path="/employee/leave/apply" element={<LeaveApplication />} />

          {/* View Leave Status — all employees */}
          <Route path="/employee/leave/status" element={<LeaveStatus />} />

          {/* Manage / Approve Leaves — Team Manager and above (rank ≥ 5)
              Changed from minRank=7: Team Managers can now see and approve
              leaves for employees ranked below them in their own department.
              Backend enforces same-dept + outrank per leave; the route only
              controls whether the page is reachable. */}
          <Route
            path="/admin/leaves"
            element={
              <RoleBasedRoute minRank={5}>
                <HRLeaveManagement />
              </RoleBasedRoute>
            }
          />
        </Route>
      </Routes>
    </div>
  );
}

export default App;